import React, { useEffect, useState } from 'react';
import { GroupCalendar } from '../../calendar/group-calendar/group-calendar';
import { Event } from '../../calendar/models/event';
import { TimelineEvent } from '../../slices/marketplace/timeline/models/timeline-event.model';
import { EventActionType } from '../../calendar/models/eventAction';
import { TimelinePartyColors } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import './MarketplaceCalendar.scss';
import { AddEventsToAnotherCalendarModal } from '../../calendar/add-events-to-another-calendar-modal/add-events-to-another-calendar-modal';
import { EngagementMeeting } from '../../slices/marketplace/meetings/models/get-engagement-conference';
import { EngagementMeetingStatus } from '../../slices/marketplace/meetings/models/enums/meeting-status.enum';
import { useStateSelector } from '../../store/selectors';
import { getEventDeadlineDate } from '../../slices/marketplace/timeline/utils/timeline-dates';

interface MarketplaceCalendarProps {
    timelineEvents: TimelineEvent[];
    startDate: Date;
    timelineId: number;
    onEventClick(eventId: string): void;
    canUserRedirectToAction: (eventId: string) => boolean;
    engagementMeetings?: EngagementMeeting[];
    onMeetingClick?: (meeting: EngagementMeeting) => void;
    currentDate?: Date;
}

export const MarketplaceCalendar = (props: MarketplaceCalendarProps) => {
    const currentUser = useStateSelector((s) => s.currentUser.user);
    const [currentDate, setCurrentDate] = useState<Date | undefined>(
        props.currentDate
    );
    const [events, setEvents] = useState<Event[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<Event | undefined>();
    const [
        isAddEventsToAnotherCalendarModalVisible,
        setIsAddEventsToAnotherCalendarModalVisible,
    ] = useState<boolean>(false);

    useEffect(() => {
        const timelineEventsMapped = props.timelineEvents.map((event) => {
            const effectiveDayStart = event.dayStart;
            const eventDate = new Date(props.startDate);
            eventDate.setDate(eventDate.getDate() + effectiveDayStart);
            const eventEnd = getEventDeadlineDate(props.startDate, event);
            const eventDeadline = getEventDeadlineDate(props.startDate, event);
            const actions = [];
            actions.push({
                id: 0,
                name: event.name,
                message: '',
                date: eventDeadline,
                type: EventActionType.Deadline,
                isAllDayLong: true,
            });
            const reminderDate = new Date(eventDeadline);
            reminderDate.setDate(reminderDate.getDate() - 2);
            const eventDateWithoutTime = new Date(eventDate.toDateString());
            const reminderDateWithoutTime = new Date(
                reminderDate.toDateString()
            );
            if (reminderDateWithoutTime > eventDateWithoutTime) {
                actions.push({
                    id: 0,
                    name: event.name,
                    message: '',
                    date: reminderDate,
                    type: EventActionType.Reminder,
                    isAllDayLong: true,
                });
            }
            const colors = TimelinePartyColors.find(
                (t) => t.id === event.party
            );

            const userActionBeClicked = props.canUserRedirectToAction(event.id);
            const onClick = () => {
                if (userActionBeClicked) {
                    props.onEventClick(event.id);
                }
            };

            return new Event(
                event.id,
                0,
                '',
                event.name,
                '',
                eventDate,
                event.isSuspendEvent ? eventEnd : eventDate,
                event.isSuspendEvent ? false : true,
                [],
                event.isSuspendEvent? [] : actions,
                [],
                'event',
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                {
                    color: event.isSuspendEvent
                        ? 'black'
                        : userActionBeClicked
                        ? '#545cd8'
                        : colors.color,
                    backgroundColor: event.isSuspendEvent
                        ? '#ced4da'
                        : colors.backgroundColor,
                    activeColor: event.isSuspendEvent
                        ? 'black'
                        : colors.activeColor,
                    activeBackgroundColor: event.isSuspendEvent
                        ? '#ced4da'
                        : colors.activeBackgroundColor,
                },
                onClick
            );
        });

        const meetingsMapped = (props.engagementMeetings ?? [])
            .filter(
                (m) =>
                    m.status === EngagementMeetingStatus.Upcoming ||
                    m.status === EngagementMeetingStatus.InProgress
            )
            .map(
                (meeting) =>
                    new Event(
                        meeting.id,
                        meeting.posterId,
                        meeting.posterName,
                        meeting.name,
                        meeting.message || '',
                        new Date(meeting.dateStart),
                        new Date(meeting.dateEnd),
                        false,
                        [],
                        [],
                        [],
                        'conference',
                        undefined,
                        meeting.isInProgress,
                        undefined,
                        undefined,
                        meeting.isRecordingAvailable,
                        undefined,
                        {
                            color: '#3b2270',
                            backgroundColor: '#d9d0f0',
                            activeColor: '#fff',
                            activeBackgroundColor: '#6a47b0',
                        },
                        () => props.onMeetingClick?.(meeting)
                    )
            );
        setEvents([...timelineEventsMapped, ...meetingsMapped]);
    }, [props.timelineEvents, props.engagementMeetings]);

    return (
        <div className="marketplace-calendar">
            <div className="pmg-calender-wrapper">
                <div className="col-title">
                    <h4>Calendar</h4>
                    <div
                        onClick={() => {
                            setIsAddEventsToAnotherCalendarModalVisible(true);
                        }}>
                        Export Events to Personal Calendar(s)
                    </div>
                </div>
            </div>
            <GroupCalendar
                events={events}
                currentDate={currentDate}
                setCurrentDate={setCurrentDate}
                setDetailsEventActions={() => {}}
                setDetailsEventActionType={() => {}}
                setSelectedEvent={setSelectedEvent}
                selectedEvent={selectedEvent}
                setShowDetailsModalEventAction={() => {}}
                groupId={0}
                notificationFlagsOnPreviousPages={0}
                notificationFlagsOnNextPages={0}
                isInitializingCalendarPage={false}></GroupCalendar>
            {isAddEventsToAnotherCalendarModalVisible ? (
                <AddEventsToAnotherCalendarModal
                    isVisible={isAddEventsToAnotherCalendarModalVisible}
                    setIsVisible={setIsAddEventsToAnotherCalendarModalVisible}
                    calendarId={props.timelineId.toString()}
                    userId={currentUser.id}
                    useUserToken={true}
                    header={
                        'Add your Engagement Calendar to your personal calendar'
                    }
                    link={`marketplace/${props.timelineId}/calendar/export/ics`}></AddEventsToAnotherCalendarModal>
            ) : (
                []
            )}
        </div>
    );
};
